import React from 'react';
import type { CampaignObjectiveId, CampaignStatus } from '../../types';
import { formatIsoDate } from '../../domain/format';
import { StatusCell } from '../CampaignColumns/CampaignColumns';
import { ObjectiveChip } from '../ObjectiveChip/ObjectiveChip';
import { Bone, TextBone } from '../CampaignSkeleton/CampaignSkeleton';
import './CampaignHeading.css';

interface Props {
  name: string;
  status: CampaignStatus;
  objective: CampaignObjectiveId;
  /** The line after the chips: the dates, then the audience (`campaignHeadingLine`). */
  line?: string;
  /** The line is still on its way (the configuration loads after the listing): a bone holds its place. */
  lineLoading?: boolean;
}

/**
 * A campaign's name and what it is at a glance — status, objective, dates and
 * audience (VOX-317). Shared by the
 * campaign detail and its edit, so editing keeps the same header and only the
 * sections and actions beside it change.
 */
export const CampaignHeading: React.FC<Props> = ({
  name,
  status,
  objective,
  line,
  lineLoading,
}) => (
  <div className="campaign-heading">
    <h1 className="campaign-heading__title" title={name}>
      {name}
    </h1>
    <div className="campaign-heading__meta">
      <StatusCell status={status} />
      <ObjectiveChip objective={objective} />
      {lineLoading ? (
        <TextBone width={180} size={12} lineHeight={20} />
      ) : (
        line && <span className="campaign-heading__line">{line}</span>
      )}
    </div>
  </div>
);

/**
 * The heading before the campaign is known: the title's line (title-2, 24/32),
 * then the status tag, the objective chip and the dates — at their sizes, so
 * the header doesn't jump when the name lands.
 */
export const CampaignHeadingSkeleton: React.FC = () => (
  <div className="campaign-heading">
    <TextBone
      width="min(320px, 70%)"
      size="var(--silia-title-2)"
      lineHeight="var(--silia-title-2-lh)"
    />
    <div className="campaign-heading__meta">
      <Bone width={72} height={22} shape="pill" />
      <Bone width={104} height={24} shape="pill" />
      <TextBone width={180} size={12} lineHeight={20} />
    </div>
  </div>
);

/** "Oct 12, 2026" / "12 oct 2026" / "12 de out. de 2026". */
const HEADING_DATE: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/**
 * "start → end · audience", or "from start · no end · audience" for a campaign
 * with no end date — without it the arrow points at nothing. The
 * dates are formatted here, at render, in the app's language `lang`.
 */
export function campaignHeadingLine(
  t: (key: string, vars?: Record<string, unknown>) => string,
  lang: string | undefined,
  dates: { startDate: string; endDate: string | null } | null,
  audience?: string,
): string {
  const day = (iso: string) => formatIsoDate(iso, lang, HEADING_DATE);
  const range = dates
    ? dates.endDate
      ? `${day(dates.startDate)} → ${day(dates.endDate)}`
      : t('detail.fromNoEnd', { date: day(dates.startDate) })
    : null;
  return [range, audience].filter(Boolean).join(' · ');
}
